<template>
  <div
    class="modal-resize-handle"
    @mousedown="startResize"
    @touchstart="startResize"
  >
    <v-icon icon="mdi-resize-bottom-right" size="small"></v-icon>
  </div>
</template>

<script>
export default {
  name: 'AResizeHandle',

  props: {
    currentWidth: {
      type: [String, Number],
      default: '100%',
    },
    currentHeight: {
      type: [String, Number],
      default: '100%',
    },
  },
  emits: ['resize', 'resize-end'],

  data() {
    return {
      isResizing: false,
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0,
    };
  },

  beforeUnmount() {
    // Clean up event listeners if component is destroyed during resize
    if (this.isResizing) {
      this.stopResize();
    }
  },

  methods: {
    startResize(event) {
      // Prevent default to avoid text selection during drag
      event.preventDefault();

      this.isResizing = true;

      // Store initial position and size
      if (event.type === 'touchstart') {
        this.startX = event.touches[0].clientX;
        this.startY = event.touches[0].clientY;
      } else {
        this.startX = event.clientX;
        this.startY = event.clientY;
      }

      // Parse current dimensions to pixels
      this.startWidth = this.parseToPixels(this.currentWidth, 'width');
      this.startHeight = this.parseToPixels(this.currentHeight, 'height');

      // Add event listeners for move and end
      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
      document.addEventListener('touchmove', this.handleResize);
      document.addEventListener('touchend', this.stopResize);

      // Add cursor style to body
      document.body.style.cursor = 'nwse-resize';
    },

    handleResize(event) {
      if (!this.isResizing) return;

      let clientX, clientY;
      if (event.type === 'touchmove') {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      // Calculate the difference
      const deltaX = clientX - this.startX;
      const deltaY = clientY - this.startY;

      // Calculate new dimensions
      const newWidth = this.startWidth + deltaX;
      const newHeight = this.startHeight + deltaY;

      // Emit resize event with new dimensions
      this.$emit('resize', { width: newWidth, height: newHeight });
    },

    stopResize() {
      if (!this.isResizing) return;

      this.isResizing = false;

      // Remove event listeners
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.removeEventListener('touchmove', this.handleResize);
      document.removeEventListener('touchend', this.stopResize);

      // Reset cursor style
      document.body.style.cursor = '';

      // Emit final resize event
      this.$emit('resize-end');
    },

    parseToPixels(value, type) {
      if (typeof value === 'number') {
        return value;
      }

      // Remove 'px' suffix if present
      const numericValue = parseFloat(value);

      // If it's a percentage, calculate based on viewport
      if (typeof value === 'string' && value.includes('%')) {
        const percent = numericValue;
        return type === 'height'
          ? (window.innerHeight * percent) / 100
          : (window.innerWidth * percent) / 100;
      }

      return numericValue || 600; // Fallback to 600px
    },
  },
};
</script>

<style scoped lang="sass">
.modal-resize-handle
	position: absolute
	bottom: 0
	right: 0
	width: 32px
	height: 32px
	cursor: nwse-resize
	display: flex
	align-items: center
	justify-content: center
	z-index: 1000
	background: rgba(0, 0, 0, 0.05)
	border-radius: 4px 0 0 0
	transition: background 0.2s ease

	&:hover
		background: rgba(0, 0, 0, 0.1)

	.v-icon
		opacity: 0.5
		transition: opacity 0.2s ease

	&:hover .v-icon
		opacity: 0.8

	@media (max-width: 768px)
		display: none !important
</style>
